import type {
  Message,
  MessageType,
  ToolCall,
  RichContent,
  ChatAction,
  ChatWidgetProps,
} from '../types';
import { MESSAGE_TYPES } from '../constants/messageTypes';
import { parseSSEData } from './sseParser';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { resolveEndpoint, getNextMessageId } from '../utils/chatHelpers';

export interface AttachmentPayload {
  name: string;
  type: string;
  size: number;
  dataUrl?: string;
}

export interface MessageResult {
  request: Message;
  response?: Message;
  raw?: unknown;
  aborted?: boolean;
}

export interface StreamingMessageParams {
  // Message content
  text: string;
  attachments?: AttachmentPayload[];
  subject?: string;
  tid?: string;
  sender?: 'user' | 'ai';
  meta?: Record<string, unknown>;

  // Endpoint config
  streamEndpoint: string | null | undefined;
  messageCreateEndpoint: string | null | undefined;
  messageEndpoint: string | null | undefined;
  apiBaseUrl: string | null | undefined;
  threadId: string | null;
  threadBase: string | null;

  // Dependencies
  dispatch: (action: ChatAction) => void;
  mountedRef: { current: boolean };
  abortControllerRef: { current: AbortController | null };
  withAuthHeaders: (h?: Record<string, string>) => Record<string, string>;
  buildPayload: (args: {
    text: string;
    attachments?: AttachmentPayload[];
    subject?: string;
    tid?: string;
    meta?: Record<string, unknown>;
  }) => Record<string, unknown>;
  buildMessagePayload?: ChatWidgetProps['buildMessagePayload'];
  handleJsonResponse: (
    data: Record<string, unknown>,
    localMsg: Message,
    atts: AttachmentPayload[]
  ) => Promise<MessageResult>;
  onMessageCreate?: (req: Message, res: Message) => void;
  trackAnalytics: (action: string, data: Record<string, unknown>) => void;
  trackOutcome?: (outcome: unknown) => void;

  // Feature flags
  enableToolVisualization: boolean;
  enableSourceCitations: boolean;
  enableThinking: boolean;
  enableRichMessages: boolean;
}

export async function createStreamingMessageFn(
  params: StreamingMessageParams
): Promise<MessageResult> {
  const {
    text,
    attachments: atts = [],
    subject: subj,
    tid,
    sender = 'user',
    meta,
    streamEndpoint,
    messageCreateEndpoint,
    messageEndpoint,
    apiBaseUrl,
    threadId,
    threadBase,
    dispatch,
    mountedRef,
    abortControllerRef,
    withAuthHeaders,
    buildPayload,
    buildMessagePayload,
    handleJsonResponse,
    onMessageCreate,
    trackAnalytics,
    trackOutcome,
    enableToolVisualization,
    enableSourceCitations,
    enableThinking,
    enableRichMessages,
  } = params;

  const endpoint = resolveEndpoint({
    primary: streamEndpoint,
    messageCreate: messageCreateEndpoint,
    message: messageEndpoint,
    threadBase,
    tid: tid || threadId,
    apiBaseUrl,
  });
  if (!endpoint) throw new Error('No message endpoint configured');

  const now = new Date();
  const localMsg: Message = {
    id: getNextMessageId(),
    sender,
    text,
    timestamp: now.toLocaleTimeString(),
    status: 'sent',
  };
  if (!(meta && typeof meta === 'object' && meta.silent === true)) {
    dispatch({ type: 'ADD_MESSAGE', message: localMsg });
  }

  const payload = {
    ...buildPayload({ text, attachments: atts, subject: subj, tid, meta }),
    stream: true,
  };

  const bodyPayload =
    typeof buildMessagePayload === 'function' ? buildMessagePayload(payload, localMsg) : payload;

  try {
    // Abort any prior stream and create new controller
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const res = await fetchWithTimeout(endpoint, {
      method: 'POST',
      headers: {
        ...withAuthHeaders(),
        Accept: 'text/event-stream',
      },
      body: JSON.stringify(bodyPayload),
      signal: controller.signal,
      timeoutMs: 60_000,
    });

    if (!res.ok) {
      let serverError = '';
      try {
        const json = (await res.json()) as Record<string, unknown>;
        if (typeof json?.error === 'string') {
          serverError = json.error;
        } else if (typeof json?.message === 'string') {
          serverError = json.message;
        }
      } catch {
        // Keep generic status code message if body is not JSON.
      }
      throw new Error(`Streaming failed: ${res.status}${serverError ? ` (${serverError})` : ''}`);
    }

    const contentType =
      typeof res.headers?.get === 'function' ? res.headers.get('content-type') || '' : '';
    const normalizedType = contentType.toLowerCase();
    const isJsonResponse =
      normalizedType.includes('application/json') ||
      normalizedType.includes('application/problem+json');

    if (isJsonResponse) {
      const data = await res.json();
      return await handleJsonResponse(data as Record<string, unknown>, localMsg, atts);
    }

    if (!res.body) {
      try {
        const data = await res.json();
        return await handleJsonResponse(data as Record<string, unknown>, localMsg, atts);
      } catch {
        throw new Error('Response body is not readable');
      }
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();

    let accumulatedText = '';
    const toolCalls: ToolCall[] = [];
    const sources: { id: string; title: string; url: string; snippet?: string }[] = [];
    let thinking = '';
    let richContent: RichContent | undefined = undefined;
    let richType: MessageType | null = null;

    let buffer = '';
    let dataLines: string[] = [];
    let streamDone = false;

    const flushDataLines = () => {
      if (dataLines.length === 0) return;
      const data = dataLines.join('\n').trim();
      dataLines = [];
      if (data === '[DONE]') {
        streamDone = true;
        return;
      }
      const event = parseSSEData(data, {
        enableToolVisualization,
        enableSourceCitations,
        enableThinking,
        enableRichMessages,
      });
      if (!event) return;
      switch (event.type) {
        case 'text_delta':
          accumulatedText += event.text;
          if (mountedRef.current) dispatch({ type: 'UPDATE_STREAMING', text: accumulatedText });
          break;
        case 'outcome':
          trackOutcome?.(event.outcome);
          break;
        case 'tool_call':
          toolCalls.push(event.toolCall);
          if (mountedRef.current) dispatch({ type: 'ADD_TOOL_CALL', toolCall: event.toolCall });
          break;
        case 'tool_result': {
          const idx = toolCalls.findIndex(t => t.id === event.id);
          if (idx >= 0) {
            toolCalls[idx].result = event.result;
            toolCalls[idx].status = 'complete';
            if (mountedRef.current)
              dispatch({ type: 'UPDATE_TOOL_CALLS', toolCalls: [...toolCalls] });
          }
          break;
        }
        case 'citation':
          sources.push(event.source);
          break;
        case 'thinking':
          thinking += event.text;
          break;
        case 'rich_content':
          richContent = event.content as RichContent;
          richType = event.messageType as MessageType;
          break;
        case 'escalated':
          if (mountedRef.current) dispatch({ type: 'SET_ESCALATED', isEscalated: true });
          break;
      }
    };

    const processLine = (line: string) => {
      if (streamDone) return;
      if (line === '') {
        flushDataLines();
        return;
      }
      if (line.startsWith('data:')) {
        let linePayload = line.slice(5);
        if (linePayload.startsWith(' ')) linePayload = linePayload.slice(1);
        dataLines.push(linePayload);
      }
    };

    const processBuffer = () => {
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        processLine(line);
        if (streamDone) break;
      }
    };

    if (!normalizedType.includes('text/event-stream')) {
      const MAX_JSON_FALLBACK_BYTES = 64_000;
      const safeTrimmed = (value: string) => value.replace(/^\uFEFF/, '').trim();
      const parseLikelyJson = (value: string) => {
        const cleaned = safeTrimmed(value);
        if (!cleaned) return null;
        try {
          return JSON.parse(cleaned);
        } catch {
          return null;
        }
      };

      const first = await reader.read();
      if (!first.done && first.value) {
        const firstText = decoder.decode(first.value, { stream: true });
        const trimmed = firstText.trimStart();
        const looksLikeJson = trimmed.startsWith('{') || trimmed.startsWith('[');
        const looksLikeSse = /(^|[\r\n])data:|(^|[\r\n])event:|(^|[\r\n]):/.test(firstText);
        if (looksLikeJson && !looksLikeSse) {
          let jsonText = firstText;
          let reachedLimit = false;
          while (true) {
            const next = await reader.read();
            if (next.done) break;
            jsonText += decoder.decode(next.value, { stream: true });
            if (jsonText.length > MAX_JSON_FALLBACK_BYTES) {
              reachedLimit = true;
              break;
            }
          }
          const maybeParsed = !reachedLimit ? parseLikelyJson(jsonText) : null;
          if (maybeParsed) {
            return await handleJsonResponse(maybeParsed as Record<string, unknown>, localMsg, atts);
          }
          buffer += jsonText;
        } else {
          buffer += firstText;
        }
      }
    }

    const streamedMessageId = `${getNextMessageId()}-ai`;
    if (!mountedRef.current)
      return {
        request: localMsg,
        response: {
          id: streamedMessageId,
          sender: 'ai',
          text: '',
          timestamp: '',
          status: 'delivered',
        } as Message,
      };
    dispatch({
      type: 'SET_STREAMING',
      message: {
        id: streamedMessageId,
        sender: 'ai',
        text: '',
        timestamp: new Date().toLocaleTimeString(),
        status: 'streaming',
        type: MESSAGE_TYPES.TEXT,
      },
    });

    if (buffer) processBuffer();

    const STREAM_IDLE_TIMEOUT = 60_000;
    while (reader && mountedRef.current && !streamDone) {
      const readResult = await Promise.race([
        reader.read(),
        new Promise<{ done: true; value: undefined }>(resolve =>
          setTimeout(() => resolve({ done: true, value: undefined }), STREAM_IDLE_TIMEOUT)
        ),
      ]);
      if (readResult.done || !mountedRef.current) break;

      buffer += decoder.decode(readResult.value, { stream: true });
      processBuffer();
    }

    if (!streamDone) {
      if (buffer) processLine(buffer);
      flushDataLines();
    }

    const finalMessage: Message = {
      id: streamedMessageId,
      sender: 'ai',
      text: accumulatedText,
      timestamp: new Date().toLocaleTimeString(),
      status: 'delivered',
      type: richType ?? MESSAGE_TYPES.TEXT,
      toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
      sources: sources.length > 0 ? sources : undefined,
      thinking: thinking || undefined,
      richContent: richContent || undefined,
    };

    if (!mountedRef.current) return { request: localMsg, response: finalMessage };

    dispatch({ type: 'CLEAR_STREAMING' });
    dispatch({ type: 'ADD_MESSAGE', message: finalMessage });
    if (
      finalMessage.type === MESSAGE_TYPES.PRODUCT_CARDS &&
      finalMessage.richContent?.products?.length
    ) {
      trackAnalytics('product_recommendations', {
        count: finalMessage.richContent.products.length,
        ids: finalMessage.richContent.products.map(p => p?.id).filter(Boolean),
      });
    }
    onMessageCreate?.(localMsg, finalMessage);
    trackAnalytics('message_sent', {
      hasAttachments: atts?.length > 0,
      streamed: true,
      hadToolCalls: toolCalls.length > 0,
    });

    return { request: localMsg, response: finalMessage };
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      dispatch({ type: 'CLEAR_STREAMING' });
      return {
        request: localMsg,
        aborted: true,
      };
    }
    dispatch({ type: 'CLEAR_STREAMING' });
    throw err;
  }
}
